/**
 * RichContent Component - Renders content with LaTeX, images, and HTML
 * Uses KaTeX for LaTeX rendering
 */

import { Fragment, useMemo } from 'react';
import { Typography, Image } from 'antd';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import DOMPurify from 'dompurify';
import { getApiFileUrl } from '../../utils/util';

const { Text } = Typography;

/**
 * Sanitize HTML before rendering. Question content is authored by teachers
 * (or AI-parsed from uploaded documents), so it must never be trusted to
 * carry scripts into other users' browsers.
 */
const sanitizeHtml = (html) => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

/**
 * Every delimiter pair we typeset, in priority order.
 *
 * Order is load-bearing: `$$...$$` must be tried before `$...$`, otherwise the
 * inline rule claims the first two dollars of a display formula and leaves a
 * stray `$` behind.
 *
 * The `$...$` arm follows the usual TeX convention — no whitespace just inside
 * either delimiter — because without it a sentence like "it costs $5 today and
 * $10 tomorrow" typesets "5 today and " as a formula. Prices in question text
 * are far more common than a teacher writing "$ x $" with padding, so this
 * trade is the right way round. Inline math also may not span a newline.
 */
const MATH_PATTERN =
  /\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\$(?!\s)((?:[^$\n]*[^\s$])|[^\s$])\$|\\\(([\s\S]+?)\\\)/g;

/**
 * Detect if text contains LaTeX formulas
 */
const containsLatex = (text) => {
  if (!text) return false;
  MATH_PATTERN.lastIndex = 0;
  return MATH_PATTERN.test(text);
};

/**
 * Show the raw source when a formula will not compile. Question content is
 * typed or pasted by teachers, so malformed LaTeX is a matter of when, not if,
 * and a KaTeX parse error must never blank out the question around it.
 */
const renderMathError = (source) => () => (
  <Text code type="danger" title="This formula could not be rendered">{source}</Text>
);

/**
 * Walk the text once, emitting plain strings and math components in document
 * order. One pass keeps block and inline formulas correctly interleaved — the
 * thing a split-then-zip approach gets wrong as soon as a line holds both.
 */
const parseLatex = (text) => {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  let match;

  MATH_PATTERN.lastIndex = 0;
  while ((match = MATH_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const [full, blockDollar, blockBracket, inlineDollar, inlineParen] = match;
    const isBlock = blockDollar !== undefined || blockBracket !== undefined;
    const math = blockDollar ?? blockBracket ?? inlineDollar ?? inlineParen;
    const Component = isBlock ? BlockMath : InlineMath;

    parts.push(
      <Component key={`math-${match.index}`} math={math} renderError={renderMathError(full)} />
    );
    lastIndex = match.index + full.length;
  }

  if (parts.length === 0) return <Text>{text}</Text>;
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // Fragment rather than span: the parts are already inline, and an extra
  // wrapper element would break up the text flow around each formula.
  return <>{parts.map((part, i) => <Fragment key={i}>{part}</Fragment>)}</>;
};

/**
 * RichContent Component
 * @param {string} content - The content to render
 * @param {string} contentType - PLAIN, LATEX, HTML, or MIXED
 * @param {string} imageUrl - Optional image URL to display
 */
const RichContent = ({ content, contentType = 'PLAIN', imageUrl }) => {
  const renderedContent = useMemo(() => {
    if (!content) return null;

    switch (contentType) {
      case 'LATEX':
        return parseLatex(content);
      case 'HTML':
        return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />;
      case 'MIXED':
        // For mixed content, try to detect and render LaTeX within HTML
        if (containsLatex(content)) {
          return parseLatex(content);
        }
        return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />;
      case 'PLAIN':
      default:
        // Even plain text might contain LaTeX accidentally
        if (containsLatex(content)) {
          return parseLatex(content);
        }
        return <Text>{content}</Text>;
    }
  }, [content, contentType]);

  // Most call sites drop this inside an inline run — an option label, a question
  // stem inside a <Text>. A <div> there is invalid nesting and breaks the line
  // flow, so only take a block wrapper when there is an image that needs one.
  if (!imageUrl) {
    return <span className="rich-content">{renderedContent}</span>;
  }

  return (
    <div className="rich-content">
      <div style={{ marginBottom: 8 }}>
        <Image
          src={getApiFileUrl(imageUrl)}
          alt="Question image"
        />
      </div>
      {renderedContent}
    </div>
  );
};

export default RichContent;

/**
 * QuestionDisplay Component - Renders a complete question with options
 */
export const QuestionDisplay = ({
  question,
  showAnswer = false,
  selectedAnswer = null,
  onSelectAnswer = null
}) => {
  const optionStyle = (option) => {
    const isCorrect = question.correctAnswer === option;
    const isSelected = selectedAnswer === option;

    let style = { padding: '8px 12px', marginBottom: 4, borderRadius: 4, cursor: onSelectAnswer ? 'pointer' : 'default' };

    if (showAnswer) {
      if (isCorrect) {
        style.backgroundColor = 'var(--color-success-bg)';
        style.border = '1px solid var(--color-success)';
      } else if (isSelected) {
        style.backgroundColor = 'var(--color-error-bg)';
        style.border = '1px solid var(--color-error)';
      }
    } else if (isSelected) {
      style.backgroundColor = 'var(--color-primary-bg)';
      style.border = '1px solid var(--color-primary)';
    }

    return style;
  };

  return (
    <div className="question-display">
      <RichContent
        content={question.questionText}
        contentType={question.contentType}
        imageUrl={question.questionImageUrl}
      />

      <div style={{ marginTop: 12 }}>
        {['A', 'B', 'C', 'D'].map(option => (
          <div
            key={option}
            style={optionStyle(option)}
            onClick={() => onSelectAnswer && onSelectAnswer(option)}
          >
            <strong>{option})</strong>{' '}
            <RichContent
              content={question[`option${option}`]}
              contentType={question.contentType}
              imageUrl={question[`option${option}ImageUrl`]}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

